import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Search, Scissors } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { Submission } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import { formatDate } from '../../lib/utils';

export default function AgencySubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const response = await api.get('/submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch =
      submission.invitation?.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.invitation?.kolProfile?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || submission.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = [
    { value: 'ALL', label: 'All status' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PENDING_AGENCY_QA', label: 'Agency QA' },
    { value: 'PENDING_BRAND_FINAL', label: 'Brand final' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REVISION_REQUESTED', label: 'Revision requested' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return <span className="status-pill status-pill-warn">Submitted</span>;
      case 'PENDING_AGENCY_QA':
        return <span className="status-pill status-pill-warn">Agency QA</span>;
      case 'PENDING_BRAND_FINAL':
        return <span className="status-pill status-pill-info">Brand final</span>;
      case 'APPROVED':
        return <span className="status-pill status-pill-pos">Approved</span>;
      case 'REVISION_REQUESTED':
        return <span className="status-pill status-pill-danger">Revision</span>;
      default:
        return <span className="status-pill status-pill-muted">{status}</span>;
    }
  };

  const videoCount = (row: Submission) =>
    row.media?.filter((m) => m.type === 'VIDEO').length ?? 0;

  const openSubmission = (row: Submission) => {
    navigate(`/agency/submissions/${toSlug(row.invitation?.job?.title || 'submission', row.id)}`);
  };

  const openAdCuts = (row: Submission) => {
    const firstVideo = row.media?.find((media) => media.type === 'VIDEO');
    if (firstVideo) {
      navigate(
        `/agency/ai-video-edits/new?submissionId=${encodeURIComponent(row.id)}&mediaId=${encodeURIComponent(firstVideo.id)}`
      );
      return;
    }
    openSubmission(row);
  };

  const columns = [
    {
      header: 'Campaign',
      accessor: (row: Submission) => (
        <div className="flex items-center gap-2 min-w-0">
          <Video className="w-5 h-5 text-glass-text-3 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-glass-text truncate">
              {row.invitation?.job?.title || 'Untitled campaign'}
            </div>
            <div className="text-sm text-glass-text-2 truncate">
              {row.invitation?.kolProfile?.name || 'KOL'} · v{row.version}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: 'Media',
      accessor: (row: Submission) => (
        <span className="text-glass-text-2">
          {row.media?.length ? `${row.media.length} file${row.media.length !== 1 ? 's' : ''}` : '—'}
          {videoCount(row) > 0 ? ` (${videoCount(row)} video)` : ''}
        </span>
      ),
    },
    {
      header: 'Submitted',
      accessor: (row: Submission) => (
        <span className="text-glass-text-2">{formatDate(row.createdAt)}</span>
      ),
    },
    {
      header: 'Status',
      accessor: (row: Submission) => getStatusBadge(row.status),
    },
    {
      header: '',
      className: 'w-[140px]',
      accessor: (row: Submission) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-glass text-glass-text shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            openAdCuts(row);
          }}
        >
          <Scissors className="w-4 h-4 mr-1" />
          Ad cuts
        </Button>
      ),
    },
  ];

  const statusCounts = submissions.reduce<Record<string, number>>((counts, submission) => {
    counts[submission.status] = (counts[submission.status] || 0) + 1;
    return counts;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">KOL submissions</h1>
        <p className="mt-1 text-xs text-glass-text-2 md:text-sm">
          <span className="md:hidden">
            {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''} ·{' '}
          </span>
          All content submitted for campaigns your agency manages. Open a row for detail or start AI video edits from a video.
        </p>
      </div>

      <div className="space-y-3 md:hidden">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-glass-text-3" />
          <Input
            type="search"
            placeholder="Search by campaign or KOL…"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="h-11 bg-glass-2 pl-9 text-glass-text"
            aria-label="Search submissions"
          />
        </div>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {statusOptions.map((option) => {
            const active = statusFilter === option.value;
            const count = option.value === 'ALL' ? submissions.length : statusCounts[option.value] || 0;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatusFilter(option.value)}
                className={`min-h-11 shrink-0 cursor-pointer rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                  active
                    ? 'border-accent2/40 bg-accent2/15 font-semibold text-accentText'
                    : 'border-glass bg-glass-2 font-medium text-glass-text-2 hover:bg-glass-3'
                }`}
                aria-pressed={active}
              >
                {option.label.replace(' status', '').replace(' requested', '')} {count}
              </button>
            );
          })}
        </div>
      </div>

      <div className="hidden glass-card p-5 md:block">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-glass-text-3 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by campaign or KOL…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-glass-2 border-glass text-glass-text"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-glass-2 border border-glass text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
      </div>

      <div className="glass-card overflow-hidden md:hidden">
        {filteredSubmissions.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Video className="mx-auto mb-3 h-10 w-10 text-glass-text-3" />
            <p className="text-sm font-medium text-glass-text-2">
              {searchTerm || statusFilter !== 'ALL'
                ? 'No submissions match your filters'
                : 'No submissions yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-glass">
            {filteredSubmissions.map((submission) => {
              const files = submission.media?.length ?? 0;
              const videos = videoCount(submission);
              return (
                <div key={submission.id} className="flex min-h-[70px] items-center gap-2 px-[13px] py-2.5">
                  <button
                    type="button"
                    onClick={() => openSubmission(submission)}
                    className="min-w-0 flex-1 cursor-pointer rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-semibold text-glass-text">
                        {submission.invitation?.job?.title || 'Untitled campaign'}
                      </span>
                      <span className="shrink-0">{getStatusBadge(submission.status)}</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-glass-text-3">
                      {submission.invitation?.kolProfile?.name || 'KOL'} · v{submission.version} ·{' '}
                      {files ? `${files} file${files === 1 ? '' : 's'}` : 'No files'}
                      {videos > 0 ? ` (${videos} video${videos === 1 ? '' : 's'})` : ''} · {formatDate(submission.createdAt)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => openAdCuts(submission)}
                    className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-glass bg-glass-2 text-glass-text-2 transition-colors hover:border-accent2/40 hover:bg-glass-3 hover:text-accent2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                    aria-label={videos > 0 ? `Create ad cuts from ${submission.invitation?.job?.title || 'submission'}` : `Open ${submission.invitation?.job?.title || 'submission'}`}
                    title={videos > 0 ? 'Create ad cuts' : 'Open submission'}
                  >
                    <Scissors className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Card className="hidden p-5 md:block">
        <CardHeader>
          <CardTitle className="text-glass-text">
            {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-glass-text-3 mx-auto mb-4" />
              <p className="text-glass-text-2 font-medium">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'No submissions match your filters'
                  : 'No submissions yet'}
              </p>
            </div>
          ) : (
            <DataTable
              data={filteredSubmissions}
              columns={columns}
              onRowClick={openSubmission}
              emptyMessage="No submissions match your filters"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
